import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IChannelAssetIdResponse,
  IChannelAssetIdSearch,
  ICreateChannelAssetId,
} from './types/query';

export const useGetChannelAssetIdListInfinite = (
  params: IChannelAssetIdSearch,
  enabled = true
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<IChannelAssetIdResponse[], DefaultError>({
    queryKey: ['channel-asset-id-list', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Channel/AssetIds',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<
        IInfinitePageResponse<IChannelAssetIdResponse>
      >(response, {
        groupName: `useGetChannelAssetIdListInfinite - ${pageParam}`,
      });

      return infiniteHandler<IChannelAssetIdResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    enabled,
  });
};

export const usePostChannelAssetId = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostChannelAssetId');
  return useMutation<
    IChannelAssetIdResponse,
    DefaultError,
    ICreateChannelAssetId
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('asset ID'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'Channel/AssetId',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostChannelAssetId',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('asset ID'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteChannelAssetId = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteChannelAssetId'
  );
  return useMutation<{}, DefaultError, string>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('asset ID'),
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: 'Channel/AssetId',
        body: { id: body },
      });

      return responseHandler(response, {
        groupName: 'useDeleteChannelAssetId',
        notFoundPath: '/channel',
      });
    },

    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('asset ID'),
        })
      );
    },
    onError: dismiss,
  });
};
